import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import MCellShiftContainerPage from '../../manufacturingLocation/components/MCellShiftContainerPage';
import {
  CELL_BOARD_INVENTORY_CENTER_ID,
  CELL_BOARD_M_CELL_COLLECTION_ID,
  CELL_BOARD_M_CELL_ID,
  CELL_BOARD_SHIFT_SELECTED,
} from '../../profile/types';
import HomePage from './HomePage';
import { useHomeLinkGroupsQuery, useHomeLinksQuery } from '../queryHooks';
import MCellMessageMenu from '../../cellBoard/components/MCellMessageMenu';
import {
  CellBoardMCellMessageDto,
  CellBoardMCellTaskDto,
} from '../../cellBoard/types';
import { useNavigation } from '../../../utils/navigation';
import { ManufacturingLocationShift } from '../../resourceTracking/types';
import { EMPTY_GUID, isEmptyOrSpaces } from '../../../utils/string-utils';
import {
  useMarkMCellTaskCompleteMutation,
  useMarkMCellTaskIncompleteMutation,
  useMCellMessagesQuery,
} from '../../cellBoard/queryHooks';
import { useMCellAlertsQuery } from '../../mCellAlert/queryHooks';
import MCellAlerts from '../../mCellAlert/components/MCellAlerts';
import { getMCellTasksFromMCellMessages } from '../../cellBoard/utils';
import MCellTaskButtonMenu from '../../cellBoard/components/MCellTaskButtonMenu';
import { DATA_REFRESH_INTERVAL } from '../../../utils/date';
import { useMCellSelector } from '../../profile/utils';
import { Box } from '@mui/material';

const NO_LOCATION_SHIFT: ManufacturingLocationShift = {
  mCellId: EMPTY_GUID,
  inventoryCenterId: EMPTY_GUID,
  mCellCollectionId: EMPTY_GUID,
  shift: 'None',
};

/**
 * Renders Home page and container
 * @constructor
 */
const HomeContainerPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { navigateToMCellMessageDetail } = useNavigation();
  // Unset until the container reports the selected location, so nothing loads for the placeholder
  const [locationShift, setLocationShift] =
    useState<ManufacturingLocationShift>();
  const { inventoryCenterId, mCellId, mCellCollectionId, shift } =
    locationShift ?? NO_LOCATION_SHIFT;
  const manufacturingLocation = {
    inventoryCenterId,
    mCellId,
    mCellCollectionId,
  };
  const locationSelected = !!locationShift;
  const mCell = useMCellSelector(mCellId);
  const regularMCellSelected = locationSelected && !!mCell && !mCell.isRollup;

  const { data: homeLinks = [], isLoading: loadingHomeLinks } =
    useHomeLinksQuery(
      manufacturingLocation,
      locationSelected,
      DATA_REFRESH_INTERVAL,
    );
  const { data: homeLinkGroups = [] } = useHomeLinkGroupsQuery(
    manufacturingLocation,
    locationSelected,
    DATA_REFRESH_INTERVAL,
  );
  const { data: mCellMessageData = [], isLoading: loadingMCellMessages } =
    useMCellMessagesQuery(mCellId, shift, regularMCellSelected);
  const { data: mCellAlertsData = [], isLoading: loadingMCellAlerts } =
    useMCellAlertsQuery(
      {
        ...manufacturingLocation,
        alertLevel: 'Elevated',
        includeEmptyMCells: true,
        sortBy: 'Alphabetical',
      },
      regularMCellSelected,
      DATA_REFRESH_INTERVAL,
    );
  const mCellMessages = regularMCellSelected ? mCellMessageData : [];
  const mCellAlerts = regularMCellSelected ? mCellAlertsData : [];
  const { mutate: markMCellTaskComplete } = useMarkMCellTaskCompleteMutation();
  const { mutate: markMCellTaskIncomplete } =
    useMarkMCellTaskIncompleteMutation();
  const showMessages = mCellMessages.length > 0;
  const mCellTasks = getMCellTasksFromMCellMessages(mCellMessages);
  const showMCellTasks = mCellTasks.length > 0;
  const mCellAlertData = mCellAlerts.find((_, i) => i === 0);
  const loading =
    loadingHomeLinks || loadingMCellMessages || loadingMCellAlerts;

  /**
   * Called when a message is clicked. Navigates to the message detail path.
   * @param mCellMessage
   */
  function handleMessageClick(mCellMessage: CellBoardMCellMessageDto) {
    navigateToMCellMessageDetail(mCellMessage.id);
  }

  /**
   * Called when a task is clicked. Navigates to the message detail path of the task.
   * @param mCellTask
   */
  function handleTaskClick(mCellTask: CellBoardMCellTaskDto) {
    navigateToMCellMessageDetail(mCellTask.mCellMessageId);
  }

  /**
   * Called when a task is marked complete/incomplete
   * @param mCellTask
   */
  function handleTaskMark(mCellTask: CellBoardMCellTaskDto) {
    if (isEmptyOrSpaces(mCellTask.completedDate)) {
      markMCellTaskComplete(mCellTask.id);
    } else {
      markMCellTaskIncomplete(mCellTask.id);
    }
  }

  return (
    <MCellShiftContainerPage
      title={t('home')}
      loading={loading}
      shiftSettingKey={CELL_BOARD_SHIFT_SELECTED}
      mCellIdSettingKey={CELL_BOARD_M_CELL_ID}
      inventoryCenterIdSettingKey={CELL_BOARD_INVENTORY_CENTER_ID}
      mCellCollectionIdSettingKey={CELL_BOARD_M_CELL_COLLECTION_ID}
      allowMCellCollection
      allowInventoryCenter
      disableShift={mCellId === EMPTY_GUID}
      onChange={setLocationShift}
      secondaryActionComponent={
        <div style={{ marginRight: theme.spacing(2) }}>
          {mCellAlertData && (
            <MCellAlerts
              mCellAlertData={mCellAlertData}
              iconButtonSize={'medium'}
            />
          )}
          {showMCellTasks && (
            <MCellTaskButtonMenu
              mCellTasks={mCellTasks}
              onTaskClick={handleTaskClick}
              onTaskMark={handleTaskMark}
            />
          )}
          {showMessages && (
            <MCellMessageMenu
              mCellMessages={mCellMessages}
              onClick={handleMessageClick}
            />
          )}
        </div>
      }
    >
      {() => (
        <Box sx={{ mx: 3 }}>
          <HomePage
            homeLinks={homeLinks}
            loading={loadingHomeLinks}
            groups={homeLinkGroups}
          />
        </Box>
      )}
    </MCellShiftContainerPage>
  );
};

export default HomeContainerPage;
